import { Injectable } from '@angular/core';
import { evaluate, parse } from 'mathjs';
import { PricingFormula, FormulaConfiguration, FormulaElement, FormulaElementType, FORMULA_ELEMENT_DEFINITIONS, FormulaValidationResult, FormulaEvaluationResult } from '../models/formula';
import { DynamicFieldsService } from './dynamic-fields.service';
import { ApiService } from '../api/api.service';

@Injectable({
  providedIn: 'root'
})
export class FormulaService {
  private readonly STORAGE_KEY = 'pricing_formulas';
  
  // Cache para evitar múltiples llamadas a la API
  private formulasCache: Map<string, PricingFormula[]> = new Map();
  private loadingPromises: Map<string, Promise<PricingFormula[]>> = new Map();

  constructor(
    private dynamicFieldsService: DynamicFieldsService,
    private api: ApiService
  ) {}

  /**
   * Obtiene todas las configuraciones de fórmulas
   */
  getAllConfigurations(): FormulaConfiguration[] {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  }

  /**
   * Obtiene las fórmulas para un provider_coverage_id y service_id específicos
   * Implementa caché para evitar múltiples llamadas a la API
   */
  getFormulas(providerCoverageId: number, serviceId: number): Promise<PricingFormula[]> {
    const cacheKey = `${providerCoverageId}_${serviceId}`;
    
    // Verificar si ya tenemos las fórmulas en caché
    if (this.formulasCache.has(cacheKey)) {
      console.log('Returning formulas from cache for:', cacheKey);
      return Promise.resolve(this.formulasCache.get(cacheKey)!);
    }

    // Verificar si ya hay una petición en curso para esta combinación
    if (this.loadingPromises.has(cacheKey)) {
      console.log('Returning existing promise for:', cacheKey);
      return this.loadingPromises.get(cacheKey)!;
    }

    // Crear nueva petición
    console.log('Making new API call for formulas:', cacheKey);
    let params = {
      "provider_coverage_id": providerCoverageId,
      "service_id": serviceId
    }
    const promise = this.api.serviceCoverageFormulas.get(params).promise()
      .then((response: any) => {
        console.log('Formulas response from backend:', response, typeof response);
        
        // Verificar que sea un array
        if (!Array.isArray(response)) {
          console.error('Formulas response is not an array:', response);
          return [];
        }
        
        // Convertir la respuesta del backend al formato esperado por el frontend
        const formulas = response.map((formula: any) => this.mapBackendFormulaToFrontend(formula));
        
        // Guardar en caché
        this.formulasCache.set(cacheKey, formulas);
        
        // Limpiar la promesa de carga
        this.loadingPromises.delete(cacheKey);
        
        return formulas;
      })
      .catch((error) => {
        console.error('Error al obtener fórmulas:', error);
        
        // Limpiar la promesa de carga en caso de error
        this.loadingPromises.delete(cacheKey);
        
        return [];
      });

    // Guardar la promesa para evitar llamadas duplicadas
    this.loadingPromises.set(cacheKey, promise);
    
    return promise;
  }

  /**
   * Guarda las fórmulas para un provider_coverage_id y service_id específicos
   * Nota: Este método no se usa en el nuevo editor, se mantiene por compatibilidad
   */
  saveFormulas(providerCoverageId: number, serviceId: number, formulas: PricingFormula[]): Promise<void> {
    console.warn('saveFormulas method is deprecated. Use individual createFormula/updateFormula methods instead.');
    
    // Invalidar caché ya que no podemos guardar múltiples fórmulas de una vez
    this.invalidateFormulasCache(providerCoverageId, serviceId);
    
    return Promise.resolve();
  }

  /**
   * Crea una nueva fórmula vacía
   */
  createEmptyFormula(providerCoverageId: number, serviceId: number): PricingFormula {
    return {
      id: this.generateFormulaId(),
      name: 'Nueva Fórmula',
      provider_coverage_id: providerCoverageId,
      service_id: serviceId,
      elements: [],
      expression: '',
      isActive: true,
      createdAt: new Date(),
      updatedAt: new Date()
    };
  }

  /**
   * Genera un ID único para una fórmula
   */
  generateFormulaId(): string {
    return 'formula_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  /**
   * Convierte los elementos de una fórmula a una expresión Math.js
   */
  elementsToExpression(elements: FormulaElement[]): string {
    return elements
      .sort((a, b) => a.position - b.position)
      .map(element => this.elementToMathJs(element))
      .join(' ');
  }

  /**
   * Convierte un elemento individual a su representación Math.js
   */
  private elementToMathJs(element: FormulaElement): string {
    const definition = FORMULA_ELEMENT_DEFINITIONS[element.type];
    
    if (definition) {
      return definition.mathJs;
    }

    switch (element.type) {
      case FormulaElementType.NUMBER:
        return element.value.toString();
      
      case FormulaElementType.DYNAMIC_FIELD:
        // Los campos dinámicos se referencian por su nombre
        return `field_${element.value.replace(/\s+/g, '_').toLowerCase()}`;
      
      default:
        return element.value?.toString() || '';
    }
  }

  /**
   * Valida una fórmula
   */
  validateFormula(formula: PricingFormula): FormulaValidationResult {
    const result: FormulaValidationResult = {
      isValid: true,
      errors: [],
      warnings: []
    };

    // Validar que la fórmula tenga elementos
    if (!formula.elements || formula.elements.length === 0) {
      result.isValid = false;
      result.errors.push('La fórmula debe tener al menos un elemento');
      return result;
    }

    // Validar que la expresión sea válida
    try {
      const expression = this.elementsToExpression(formula.elements);
      parse(expression);
    } catch (error) {
      result.isValid = false;
      result.errors.push(`Error de sintaxis: ${error instanceof Error ? error.message : 'Error desconocido'}`);
    }

    // Validar paréntesis balanceados
    const openParens = formula.elements.filter(e => e.type === FormulaElementType.PARENTHESIS_OPEN).length;
    const closeParens = formula.elements.filter(e => e.type === FormulaElementType.PARENTHESIS_CLOSE).length;
    
    if (openParens !== closeParens) {
      result.isValid = false;
      result.errors.push('Los paréntesis no están balanceados');
    }

    // Validar que los campos dinámicos existan
    const dynamicFieldElements = formula.elements.filter(e => e.type === FormulaElementType.DYNAMIC_FIELD);
    this.dynamicFieldsService.getFieldsForService(formula.service_id)
      .then(availableFields => {
        for (const fieldElement of dynamicFieldElements) {
          const fieldExists = availableFields.some(f => f.name === fieldElement.value);
          if (!fieldExists) {
            result.warnings.push(`El campo "${fieldElement.value}" no existe en la configuración del servicio`);
          }
        }
      })
      .catch(error => {
        console.error('Error al validar campos dinámicos:', error);
        result.warnings.push('Error al validar los campos dinámicos');
      });

    return result;
  }

  /**
   * Evalúa una fórmula con un contexto dado
   */
  evaluateFormula(formula: PricingFormula, context: any): FormulaEvaluationResult {
    const startTime = performance.now();

    try {
      // Validar la fórmula primero
      const validation = this.validateFormula(formula);
      if (!validation.isValid) {
        return {
          success: false,
          error: validation.errors.join(', ')
        };
      }

      // Preparar el scope para Math.js
      const scope = this.prepareEvaluationScope(formula, context);
      
      // Obtener la expresión
      const expression = this.elementsToExpression(formula.elements);
      console.log('🔧 Expresión a evaluar:', expression);
      
      // Evaluar la expresión
      const result = evaluate(expression, scope);
      
      const endTime = performance.now();

      return {
        success: true,
        result: typeof result === 'number' ? result : parseFloat(result),
        executionTime: endTime - startTime
      };

    } catch (error) {
      const endTime = performance.now();
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Error desconocido',
        executionTime: endTime - startTime
      };
    }
  }

  /**
   * Prepara el scope de variables para la evaluación
   */
  private prepareEvaluationScope(formula: PricingFormula, context: any): any {
    const scope: any = {};

    // Agregar todos los campos del contexto directamente al scope
    Object.keys(context).forEach(key => {
      const value = context[key];
      
      // Agregar el valor con el nombre original
      scope[key] = value;
      
      // Agregar con prefijo field_ (como lo espera la fórmula)
      const scopeName = `field_${key.replace(/\s+/g, '_').toLowerCase()}`;
      scope[scopeName] = value;
      
      // Agregar en minúsculas sin espacios
      const lowerName = key.toLowerCase().replace(/\s+/g, '_');
      scope[lowerName] = value;
      
      console.log(`🔧 Scope preparado: ${key} = ${value}, ${scopeName} = ${value}, ${lowerName} = ${value}`);
    });

    // Agregar constantes predefinidas
    scope.base_price = context.basePrice || 0;
    scope.distance_km = context.constants?.distance_km || 0;
    scope.time_hours = context.constants?.time_hours || 0;
    scope.percentage = context.constants?.percentage || 0;

    console.log('🔧 Scope final preparado:', scope);
    return scope;
  }

  /**
   * Obtiene los elementos disponibles para construir fórmulas
   */
  getAvailableElements(serviceId: number): { [category: string]: FormulaElement[] } {
    const elements: { [category: string]: FormulaElement[] } = {
      operators: [],
      functions: [],
      conditionals: [],
      comparators: [],
      grouping: [],
      constants: [],
      dynamicFields: []
    };

    // Agregar elementos predefinidos
    Object.keys(FORMULA_ELEMENT_DEFINITIONS).forEach(key => {
      const type = key as FormulaElementType;
      const definition = FORMULA_ELEMENT_DEFINITIONS[type];
      
      const element: FormulaElement = {
        id: this.generateElementId(),
        type: type,
        value: definition.symbol,
        displayName: definition.displayName,
        position: 0
      };

      if (elements[definition.category]) {
        elements[definition.category].push(element);
      }
    });

    // Agregar campos dinámicos del servicio
    this.dynamicFieldsService.getFieldsForService(serviceId)
      .then(dynamicFields => {
        dynamicFields.forEach(field => {
          elements.dynamicFields.push({
            id: this.generateElementId(),
            type: FormulaElementType.DYNAMIC_FIELD,
            value: field.name,
            displayName: field.name,
            position: 0
          });
        });
      })
      .catch(error => {
        console.error('Error al cargar campos dinámicos:', error);
      });

    return elements;
  }

  /**
   * Genera un ID único para un elemento
   */
  generateElementId(): string {
    return 'element_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  /**
   * Clona un elemento para uso en drag & drop
   */
  cloneElement(element: FormulaElement): FormulaElement {
    return {
      ...element,
      id: this.generateElementId()
    };
  }

  /**
   * Elimina las fórmulas para un provider_coverage_id y service_id específicos
   */
  removeFormulas(providerCoverageId: number, serviceId: number): void {
    const configurations = this.getAllConfigurations();
    const filteredConfigurations = configurations.filter(
      c => !(c.provider_coverage_id === providerCoverageId && c.service_id === serviceId)
    );
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(filteredConfigurations));
  }

  /**
   * Verifica si existen fórmulas para un provider_coverage_id y service_id
   */
  hasFormulas(providerCoverageId: number, serviceId: number): Promise<boolean> {
    return this.getFormulas(providerCoverageId, serviceId).then(formulas => formulas.length > 0);
  }

  /**
   * Invalida el caché de fórmulas para una combinación específica
   */
  invalidateFormulasCache(providerCoverageId: number, serviceId: number): void {
    const cacheKey = `${providerCoverageId}_${serviceId}`;
    console.log('Invalidating formulas cache for:', cacheKey);
    this.formulasCache.delete(cacheKey);
    this.loadingPromises.delete(cacheKey);
  }

  /**
   * Invalida todo el caché de fórmulas
   */
  invalidateAllFormulasCache(): void {
    console.log('Invalidating all formulas cache');
    this.formulasCache.clear();
    this.loadingPromises.clear();
  }

  /**
   * Mapea una fórmula del backend al formato del frontend
   */
  private mapBackendFormulaToFrontend(backendFormula: any): PricingFormula {
    return {
      id: backendFormula.id.toString(),
      name: backendFormula.formula_name,
      expression: backendFormula.formula_expression || '',
      elements: backendFormula.formula_elements || [],
      provider_coverage_id: backendFormula.provider_coverage_id,
      service_id: backendFormula.service_id,
      description: backendFormula.description || '',
      createdAt: new Date(backendFormula.created_at),
      updatedAt: new Date(backendFormula.updated_at),
      isActive: backendFormula.is_active
    };
  }

  /**
   * Mapea una fórmula del frontend al formato del backend
   */
  private mapFrontendFormulaToBackend(frontendFormula: PricingFormula, providerCoverageId: number, serviceId: number): any {
    return {
      formula_name: frontendFormula.name,
      formula_expression: frontendFormula.expression,
      formula_elements: frontendFormula.elements || [],
      provider_coverage_id: providerCoverageId,
      service_id: serviceId,
      is_active: frontendFormula.isActive !== false,
      description: frontendFormula.description || ''
    };
  }

  /**
   * Parsea elementos desde una expresión de fórmula
   * Esto es una implementación básica que se puede mejorar
   */
  private parseElementsFromExpression(expression: string): FormulaElement[] {
    // Implementación básica - se puede mejorar para parsear correctamente
    const elements: FormulaElement[] = [];
    const tokens = expression.split(' ');
    
    tokens.forEach((token, index) => {
      const element: FormulaElement = {
        id: this.generateElementId(),
        type: this.getTokenType(token),
        value: token,
        displayName: token,
        position: index
      };
      elements.push(element);
    });
    
    return elements;
  }

  /**
   * Determina el tipo de elemento basado en el token
   */
  private getTokenType(token: string): FormulaElementType {
    // Implementación básica - se puede mejorar
    if (['+', '-', '*', '/', '×', '÷'].includes(token)) {
      return FormulaElementType.MULTIPLY; // Simplificado
    }
    if (['(', ')'].includes(token)) {
      return token === '(' ? FormulaElementType.PARENTHESIS_OPEN : FormulaElementType.PARENTHESIS_CLOSE;
    }
    if (!isNaN(Number(token))) {
      return FormulaElementType.NUMBER;
    }
    return FormulaElementType.DYNAMIC_FIELD; // Por defecto
  }

  /**
   * Crea una nueva fórmula en el backend
   */
  async createFormula(formula: PricingFormula): Promise<PricingFormula> {
    try {
      console.log('Creando fórmula:', formula);
      const payload = this.mapFrontendFormulaToBackend(formula, formula.provider_coverage_id, formula.service_id);
      console.log('Payload para crear:', payload);

      const response = await this.api.serviceCoverageFormulas.create(payload).promise();
      console.log('Create formula response:', response);
      
      // Invalidar caché para refrescar la lista
      if (formula.provider_coverage_id && formula.service_id) {
        this.invalidateFormulasCache(formula.provider_coverage_id, formula.service_id);
      }
      
      return this.mapBackendFormulaToFrontend(response);
    } catch (error) {
      console.error('Error al crear fórmula:', error);
      throw error;
    }
  }

  /**
   * Actualiza una fórmula existente en el backend
   */
  async updateFormula(formula: PricingFormula): Promise<PricingFormula> {
    try {
      if (!formula.id) {
        throw new Error('ID de fórmula requerido para actualización');
      }

      console.log('Actualizando fórmula:', formula);
      console.log('ID de fórmula (string):', formula.id);
      console.log('ID de fórmula (number):', parseInt(formula.id));

      const payload = this.mapFrontendFormulaToBackend(formula, formula.provider_coverage_id, formula.service_id);
      console.log('Payload para actualizar:', payload);

      const response = await this.api.serviceCoverageFormulas.update(payload, parseInt(formula.id)).promise();
      console.log('Update formula response:', response);
      
      // Invalidar caché para refrescar la lista
      if (formula.provider_coverage_id && formula.service_id) {
        this.invalidateFormulasCache(formula.provider_coverage_id, formula.service_id);
      }
      
      return this.mapBackendFormulaToFrontend(response);
    } catch (error) {
      console.error('Error al actualizar fórmula:', error);
      throw error;
    }
  }

  /**
   * Elimina una fórmula del backend
   */
  async deleteFormula(formulaId: number): Promise<void> {
    try {
      await this.api.serviceCoverageFormulas.delete(formulaId).promise();
      console.log('Formula deleted:', formulaId);
      
      // Invalidar todo el caché ya que no sabemos qué combinación se afectó
      this.invalidateAllFormulasCache();
    } catch (error) {
      console.error('Error al eliminar fórmula:', error);
      throw error;
    }
  }

  /**
   * Calcula el resultado de una fórmula usando el endpoint del backend
   */
  async calculateFormula(formulaId: number, fieldValues: any): Promise<any> {
    try {
      console.log('Calculando fórmula:', { formulaId, fieldValues });
      
      const response = await this.api.serviceCoverageFormulas.calculate(formulaId, fieldValues).promise();
      console.log('Resultado del cálculo:', response);
      
      return response;
    } catch (error) {
      console.error('Error al calcular fórmula:', error);
      throw error;
    }
  }
}